import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, VStack, SimpleGrid, Center, Spinner } from '@chakra-ui/react';
import { HiAcademicCap, HiShieldCheck } from 'react-icons/hi2';
import { OnePageHeader as PageHeader } from '@/components/layout/OnePageHeader';
import { RosterStatusBadge } from '@/components/StudentStatusBadge';
import { useNotificationsDrawer } from '@/contexts/NotificationsDrawerContext';
import { guardianStudentName } from '@/services/reunification';
import { useMyStudents, useUnreadCount } from '@/hooks/useReunification';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useVisitorManagementEnabled } from '@/hooks/useVisitorManagement';
import { TabMenu, type TabItem } from '@themes/aegix';
import { PreCheckInView } from '@/components/PreCheckInView';
import { CarRiderQueueView } from '@/components/CarRiderQueueView';
import { PickupDelegationView } from '@/components/PickupDelegationView';
import { StudentCheckInView } from '@/components/StudentCheckInView';

export default function Students() {
  const { t } = useTranslation();
  useDocumentTitle(t('students.title'));
  const { open: openNotifications } = useNotificationsDrawer();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.unread_count ?? 0;
  const { data, isLoading } = useMyStudents();
  const myStudents = data?.results ?? [];
  const isVmEnabled = useVisitorManagementEnabled();

  const [activeTab, setActiveTab] = useState('students');

  // Reset activeTab back to 'students' if VM is disabled (e.g. flag turned off mid-session)
  useEffect(() => {
    if (!isVmEnabled && activeTab !== 'students') {
      setActiveTab('students');
    }
  }, [isVmEnabled, activeTab]);

  const tabs: TabItem[] = [
    { value: 'students', label: t('students.tabLabel'), 'data-e2e': 'tab-students' },
    ...(isVmEnabled
      ? [
          { value: 'pre-checkin', label: t('students.preCheckInTab'), 'data-e2e': 'tab-pre-checkin' },
          { value: 'car-rider', label: t('students.carRiderTab'), 'data-e2e': 'tab-car-rider' },
          { value: 'pickup-delegation', label: t('students.pickupDelegationTab'), 'data-e2e': 'tab-pickup-delegation' },
          { value: 'check-in', label: t('students.checkInTab'), 'data-e2e': 'tab-checkin' },
        ]
      : []),
  ];

  return (
    <Box>
      <PageHeader
        icon={HiAcademicCap}
        title={t('students.title')}
        subtitle={t('students.subtitle')}
        showSearch={false}
        showNotifications={true}
        onNotificationClick={openNotifications}
        notificationCount={unreadCount}
        showColorMode={false}
      />

      <TabMenu
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        e2eTitle="StudentsTabs"
      />

      <Box p={{ base: 4, md: 6 }}>
        {activeTab === 'students' && (
          <>
            {isLoading ? (
              <Center py={12}>
                <Spinner size="lg" />
              </Center>
            ) : myStudents.length > 0 ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                {myStudents.map((student) => (
                  <Box
                    key={student.public_id}
                    bg="surface.primary"
                    border="1px solid"
                    borderColor="border.secondary"
                    borderRadius="lg"
                    overflow="hidden"
                    data-e2e={`student-card-${student.public_id}`}
                  >
                    <Box h={2} bg="button.primary" />
                    <Box p={4}>
                      <Box mb={2}>
                        <Text fontSize="lg" fontWeight="600" color="text.primary">
                          {guardianStudentName(student)}
                        </Text>
                        <Text fontSize="sm" color="text.secondary">
                          {student.grade} {student.homeroom ? `\u2014 ${student.homeroom}` : ''}
                        </Text>
                        {student.school_name ? (
                          <Text fontSize="sm" color="text.secondary">
                            {student.school_name}
                          </Text>
                        ) : null}
                      </Box>

                      {/* The guardian students payload carries the
                          roster enrollment status (active/inactive/
                          transferred/withdrawn), not a per-event
                          reunification status, and has no updated_at. */}
                      {student.status ? (
                        <Box pt={3} borderTopWidth="1px" borderColor="border.line">
                          <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={1}>
                            {t('students.currentStatus')}
                          </Text>
                          <RosterStatusBadge status={student.status} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Center py={12}>
                <VStack gap={2}>
                  <HiShieldCheck size={32} />
                  <Text fontSize="lg" color="text.secondary">
                    {t('students.noStudentsLinked')}
                  </Text>
                  <Text fontSize="sm" color="text.secondary">
                    {t('students.contactSchool')}
                  </Text>
                </VStack>
              </Center>
            )}
          </>
        )}

        {isVmEnabled && activeTab === 'pre-checkin' && <PreCheckInView />}
        {isVmEnabled && activeTab === 'car-rider' && <CarRiderQueueView />}
        {isVmEnabled && activeTab === 'pickup-delegation' && <PickupDelegationView />}
        {isVmEnabled && activeTab === 'check-in' && <StudentCheckInView />}
      </Box>
    </Box>
  );
}
